import { type FC, type ReactNode, useMemo } from 'react';
import { ItemPrice } from './ItemPrice';
import { cn } from '../utils/cn';
import { parseItemDetails, abbreviateWearCondition } from '../utils/formatters';

interface PrizeNamePriceRowProps {
  hashName: string;
  fallbackName: string;
  credits: number;
  steamPrice?: number | null;
  layout: 'mobile' | 'desktop' | 'compact';
  priceCaption?: ReactNode;
  showQualityBadges?: boolean;
  className?: string;
}

export const PrizeNamePriceRow: FC<PrizeNamePriceRowProps> = ({
  hashName,
  fallbackName,
  credits,
  steamPrice,
  layout,
  priceCaption,
  showQualityBadges = false,
  className,
}) => {
  const parsed = useMemo(() => parseItemDetails(hashName), [hashName]);

  const isGraffitiOrSticker = parsed.weaponName === 'Graffiti' || parsed.weaponName === 'Sticker';
  const conditionLabel = parsed.condition
    ? isGraffitiOrSticker
      ? parsed.condition
      : abbreviateWearCondition(parsed.condition)
    : '';

  const isMobile = layout === 'mobile';
  const isCompact = layout === 'compact';
  const badges = showQualityBadges && (parsed.isStar || parsed.isStatTrak || parsed.isSouvenir);
  const hasTopRow = Boolean(parsed.weaponName || conditionLabel || badges);

  return (
    <div className={cn('flex flex-col', className)}>
      {hasTopRow && (
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase leading-4 tracking-[1px] text-white/80">
          {parsed.weaponName && <span className="min-w-0 truncate">{parsed.weaponName}</span>}
          {showQualityBadges && parsed.isStar && <span className="shrink-0 text-price">★</span>}
          {showQualityBadges && parsed.isStatTrak && <span className="shrink-0 text-price">StatTrak™</span>}
          {showQualityBadges && parsed.isSouvenir && <span className="shrink-0 text-price">Souvenir</span>}
          {conditionLabel && <span className="shrink-0 text-white">{conditionLabel}</span>}
        </div>
      )}

      <h3
        className={cn(
          'm-0 truncate font-bold tracking-[-0.4px] text-white',
          isCompact ? 'text-base leading-6' : 'leading-7',
          isMobile ? 'text-2xl' : isCompact ? '' : 'text-xl',
        )}
      >
        {parsed.skinName || parsed.weaponName || fallbackName}
      </h3>

      <div
        className={cn(
          'flex items-center whitespace-nowrap text-white',
          isCompact ? 'gap-1 text-sm font-semibold leading-5' : 'leading-7',
          isMobile ? 'mt-1 gap-1 text-lg font-bold' : isCompact ? '' : 'mt-1.5 gap-1.5 text-base font-semibold',
        )}
      >
        {priceCaption && <span className="text-xs font-normal text-white/80">{priceCaption}</span>}
        <ItemPrice
          credits={credits}
          steamPrice={steamPrice}
          creditsIconSize={isMobile ? 16 : isCompact ? 13 : 14}
          className={isMobile || isCompact ? undefined : 'gap-1.5'}
        />
      </div>
    </div>
  );
};
